/* eslint-disable @typescript-eslint/no-explicit-any */
import ModalRestricted from '@components/modal/modal-restricted';
import HeaderPractitioner from '@components/page-wrapper/header-practititoner/header-practitioner';
import PageWrapper from '@components/page-wrapper/page-wrapper';
import PractitionerMenu from '@components/page-wrapper/practitioner-menu/practitioner-menu';
import UpdateAlert from '@components/update-alert/update-alert';
import VoiceNotificationContainer from '@components/voice-call/components/voice-notification-container';
import { useDate } from '@customHooks/use-date';
import useInactivityTime from '@customHooks/useInactiveTimer';
import { cleanChatSocketQueue } from '@redux/slices/socketChat/chatChats';
import { cleanContactSocketQueue } from '@redux/slices/socketChat/chatContacts';
import { setChatSocketState, setContactsSocketState } from '@redux/slices/socketChat/storeForRender/chatSocketState';
import { RootState } from '@redux/store';
import { getMaintenanceSchedule } from '@services/practitioner/maintenance';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { environment } from '../../environments/environment';
import { io, Socket } from 'socket.io-client';
import { finishCommunication, logout } from '../../functions/chat-controller/chatController';
import { socket_url, socket_path} from '../../environments/voice-call-variables';
import { setLogged } from '@redux/slices/socketChat/loggedValidation';
import VideoNotificationContainer from '@components/video-call/components/video-notification-container';
// import { notify } from '@functions/notification';
import VoiceCallManager from '@components/voice-call/voicecall-manager';
import { activateCurrentPractitionerInOrganization } from '@services/organization-structure';
import { notify } from '@functions/notification';
import { useTranslation } from 'react-i18next';
import { cleanInvitation } from '@redux/slices/organization/related-organizations-slice';
import useAppOrigin from '@customHooks/use-app-origin';
import { cleanChatContactStatus } from '@redux/slices/socketChat/chatStatus';

export const MenuActive = createContext({
    setMenuActive: (active: string, disableMenu = false, route = '') => null,
    sectionActive: 'schedule',
    disableMenu: false,
    route: ''
})

interface RestrictedContext {
    restricted: (isRestricted: boolean, callback: () => void) => void
}

export const Restricted = createContext<RestrictedContext>({
    restricted: (isRestricted: boolean, callback: () => void) => null
})

export const SocketContext = createContext<{socket: Socket | null}>({
    socket: null
})

const socket = io( socket_url, { path: socket_path } )

const PractitionerModule = ({ routes }: any) => {
    const { t } = useTranslation("common");

    const session = useSelector((state: RootState) => state.session);
    const { invited } = useSelector((state: RootState) => state.relatedOrganization);
    const organization = useSelector((state: RootState) => state.organizations);
    const [hide, setHide] = useState<boolean>(false);
    const [active, setActive] = useState<string>('calendar');
    const [disableMenu, setDisableMenu] = useState<boolean>(false);
    const [openModalRestrict, setOpenModalRestrict] = useState<boolean>(false);
    const [func, setFunc] = useState<{callback: () => void}>({callback: () => null});
    const [route, setRoute] = useState<string>('');
    const [isMaintenance, setIsMaintenance] = useState<boolean>(false);
    const [startDate, setStartDate] = useDate({formatHour: 12})
    const [endDate, setEndDate] = useDate({formatHour: 12})
    const dispatch = useDispatch();
    const history = useNavigate()
    const { appOrigin } = useAppOrigin()

    useEffect(() => {
        window.addEventListener('beforeunload', () => {
            dispatch(setContactsSocketState([]))
            dispatch(cleanContactSocketQueue([]));
            dispatch(cleanChatSocketQueue());
            dispatch(cleanChatContactStatus());
            dispatch(setChatSocketState([]));
            dispatch(setLogged(false));
            logout({});
            finishCommunication();
        })
        return(() => {
        window.removeEventListener('beforeunload', () => {
            console.log("done")
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [ AlertFinishTime ] = useInactivityTime(environment.idleTimeLimit)

    const setRestriction = (isRestricted: boolean, callback: () => void) => {
        setOpenModalRestrict(isRestricted)
        setFunc({callback})
    }

    const changeActive = (section: string, disableMenu = false, route = '') => {
        setActive(section);
        setDisableMenu(disableMenu);
        setRoute(route)
        return null;
    };

    const handleResize = useCallback(() => {
        if(window.innerWidth < 1023) {
            setHide(true);
        } else {
            setHide(false)
        }
    }, [])

    useEffect(() => {
        handleResize()
        window.addEventListener("resize", handleResize, false);
        return () =>  {
            window.removeEventListener("resize", handleResize);
        }
    }, [handleResize]);

    useEffect(() => {        
        const getMaintenance = async () => {
            try {
                const schedule = await getMaintenanceSchedule();

                const startdate = new Date(`${schedule.date}T${schedule.startHour}`)
                const enddate = new Date(`${schedule.date}T${schedule.endhour}`)

                if (startdate.getTimezoneOffset() < 0) {
                    setStartDate(new Date(startdate.setMinutes((startdate.getTimezoneOffset() * -1) + 300 + startdate.getMinutes())))
                    setEndDate(new Date(enddate.setMinutes((enddate.getTimezoneOffset() * -1) + 300 + enddate.getMinutes())))
                } else {
                    setStartDate(new Date(startdate.setMinutes(300 - startdate.getTimezoneOffset() + startdate.getMinutes())))
                    setEndDate(new Date(enddate.setMinutes(300 - enddate.getTimezoneOffset() + enddate.getMinutes())))
                }

                const maintenance = (schedule.daily && new Date() < new Date(new Date().setHours(enddate.getHours()))) ||
                    (new Date().getDate() === startdate.getDate() && new Date() < enddate)

                setIsMaintenance(maintenance)
            } catch (error) {
                console.log("error:", error)
            }
        }

        getMaintenance()
    }, [setEndDate, setStartDate]);
    

    useEffect(() => {
        if(!appOrigin) return
        if(session && !session.plan && !session.isPremium && appOrigin !== "hubio" && organization?.predeterminedDrProx){
            // notify({message: "Necesitas una suscripción al Plan Premium para acceder al sistema", messageType: "error"})
            history("/subscription")
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session, appOrigin, organization])

    // If user logs in and has an invitation to be accepted, join to the organization
    useEffect(() => {
        if(!invited) return
        const id = invited.organizationId || invited.organizationIdLegacy
        if(!id)
        {
            console.error("Invited redux value is set, but its ID values are empty")
            return
        }

        activateCurrentPractitionerInOrganization(id)
            .then((resp) => {
                console.debug("activateCurrentPractitionerInOrganization", resp)
                notify({ message: t("organization:notify.status.success"), messageType: "success" })
            })
            .catch(err => {
                console.error("activateCurrentPractitionerInOrganization", err)
                notify({ message: t("organization:notify.status.error"), messageType: "error" })
            })
            .then(() => {
                dispatch(cleanInvitation())
            })
    }, [dispatch, invited, t])

    return (
        <SocketContext.Provider value={{socket}}>
            <VoiceCallManager />
            <Restricted.Provider value={{restricted: (isRestricted: boolean, callback: () => void) => setRestriction(isRestricted, callback)}}>
                <ModalRestricted open={openModalRestrict} action={func?.callback} onClose={() => setOpenModalRestrict(false)}/>
                <MenuActive.Provider value={{
                    setMenuActive: (active, disableMenu = false, route = '') => changeActive(active, disableMenu, route),
                    sectionActive: active,
                    disableMenu: disableMenu,
                    route: route
                }}>
                    <PageWrapper
                        bodyClass={`BodySignedIn${isMaintenance ? '-Maintenance' : ''}`}
                        hideFooter={true}
                        header={
                            <HeaderPractitioner
                                hide={hide}
                                setHide={setHide}
                                classSection1="Display"
                                classSection2="HeaderButtonCtr"
                                classSection3="HeaderButtonCtr"
                            />
                        }
                        menuComponent={
                            hide === false ? (
                                <PractitionerMenu hide={hide} setHide={setHide} />
                            ) : undefined
                        }
                    >
                        <>
                            <AlertFinishTime />
                            <VoiceNotificationContainer />
                            <VideoNotificationContainer />
                            {isMaintenance && <UpdateAlert endDate={endDate} startDate={startDate} />}
                            {/* <Switch key="practitioner-module-switch">
                                {routes.map((route: any) => {
                                    return session && session.loggedIn === true ? (
                                        <RouteWithSubRoutes key={route.key} {...route} />
                                    ) : (
                                        <Redirect key="not-allowed" to="/login" />
                                    );
                                })}
                                <Route key="not-found" component={() => <h1>Not Found!</h1>} />
                            </Switch> */}
                            <Outlet />
                        </>
                    </PageWrapper>
                </MenuActive.Provider>
            </Restricted.Provider>
        </SocketContext.Provider>
    );
};

// export function RouteWithSubRoutes(route: any) {
//     return (
//         <Route
//             path={route.path}
//             exact
//             render={(props) => (
//                 <route.component
//                     key={route.key}
//                     {...props}
//                     routes={route.routes}
//                 />
//             )}
//         />
//     );
// }

export default PractitionerModule;
